/**
 * Ссылка «доехать до точки».
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ПОЧЕМУ https, А НЕ dgis://
 * ─────────────────────────────────────────────────────────────────────────
 * До этого во всех четырёх местах стояла схема `geo:` — андроидная, и в
 * браузере она не открывает ничего. Приложение живёт на
 * scout.aida-sharing.kz, то есть в браузере оно и открывается.
 *
 * Схема `dgis://` чинит только половину: с установленным 2ГИС она работает,
 * а без него навигация просто не происходит, и отличить это от сломанной
 * кнопки скаут не может — событие ошибки браузер не даёт.
 *
 * https-ссылку установленное приложение перехватывает само (Android App Links
 * / iOS Universal Links), а без него открывается сайт 2ГИС с тем же
 * маршрутом. Оба исхода рабочие, и ни один не выглядит поломкой.
 *
 * ⚠ ПОРЯДОК КООРДИНАТ — ДОЛГОТА, ШИРОТА. Как в GeoJSON и turf, но наоборот
 * к тому, как их произносят и как они лежат в наших типах. Перепутанные
 * местами координаты Алматы (76.9, 43.2) дают точку в Индийском океане —
 * ссылка при этом рабочая, маршрут строится, и заметить это можно только
 * приехав. Поэтому на порядок есть тест.
 */
import { distanceM, type LatLng } from '@/lib/geo'

/**
 * Шесть знаков после запятой — примерно 11 см. Больше не несёт смысла, а
 * `toFixed` заодно снимает экспоненциальную запись, которую JS выдаёт на
 * очень малых числах и которую 2ГИС не разберёт.
 */
function coord(n: number): string {
  return n.toFixed(6)
}

/**
 * Маршрут до точки из текущего положения.
 *
 * Пустой первый элемент в `points` — это «откуда» = «я»: 2ГИС подставит
 * позицию сам. Разделитель `|` кодируется, иначе часть браузеров и прокси
 * режут его как недопустимый в пути символ.
 */
export function navUrl(p: LatLng): string {
  return `https://2gis.kz/directions/points/%7C${coord(p.lng)},${coord(p.lat)}`
}

/**
 * Сама точка в 2ГИС, без маршрута — «где стоит» в карточке задания сборщика
 * (формат из ТЗ маршрутов: `/almaty/geo/<lng>,<lat>`).
 */
export function pointUrl(p: LatLng): string {
  return `https://2gis.kz/almaty/geo/${coord(p.lng)},${coord(p.lat)}`
}

/**
 * Маршрут по нескольким точкам — «Маршрут по N великам» (§2.3).
 *
 * Каждая следующая точка отделена тем же закодированным `%7C`, что и «от
 * меня» у одиночного маршрута: тот же путь, тот же символ, по той же
 * причине (см. шапку файла).
 */
export function multiNavUrl(points: readonly LatLng[]): string {
  const segments = points.map((p) => `${coord(p.lng)},${coord(p.lat)}`)
  return `https://2gis.kz/directions/points/%7C${segments.join('%7C')}`
}

/**
 * Порядок «ближайший следующий» — жадный маршрут от точки `from` через все
 * `points`. Не оптимальный (это NP-трудная задача коммивояжёра), а
 * достаточный: скаут объезжает 3–5 байков за раз, и разница с оптимальным
 * маршрутом на таком масштабе не стоит внимания, которое отняла бы точная
 * оптимизация.
 */
export function nearestNeighbourOrder<T extends LatLng>(
  from: LatLng,
  points: readonly T[],
): T[] {
  const remaining = [...points]
  const ordered: T[] = []
  let current: LatLng = from

  while (remaining.length > 0) {
    let bestIndex = 0
    let bestDistance = Number.POSITIVE_INFINITY
    for (let i = 0; i < remaining.length; i += 1) {
      const d = distanceM(current, remaining[i]!)
      if (d < bestDistance) {
        bestDistance = d
        bestIndex = i
      }
    }
    const next = remaining.splice(bestIndex, 1)[0]!
    ordered.push(next)
    current = next
  }

  return ordered
}
